import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import MaskedInput from 'react-maskedinput';
import { ParagraphNormal } from '../Paragraph';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
  width: ${({ width }) => width ?? '100%'};
  position: relative;
`;

const TextInputField = styled.input`
  padding: ${({ hasPrefix }) => (hasPrefix ? '12px 24px' : '12px')};
  border: 1px solid
    ${({ hasError }) =>
      hasError
        ? themeColors.colorErrorMessage
        : themeColors.colorBorderPrimary};
  border-width: 1px;
  border-style: solid;
  border-radius: ${variables.borderRadius.borderRadius};
  color: ${themeColors.colorTextSecondary};
  font-weight: 400;
  font-size: ${({ fontSize }) =>
    fontSize ?? variables.typography.p.small.fontSize};
  text-transform: ${({ hasSuggestion }) =>
    hasSuggestion ? 'capitalize' : 'none'};
  outline: none;
  text-align: ${({ textAlign }) => textAlign ?? 'left'};
  background: transparent;
  z-index: ${({ hasSuggestion }) => (hasSuggestion ? 1 : 'initial')};

  &:focus {
    border-color: ${themeColors.colorSvgSemidark};
  }

  &:disabled {
    background-color: ${themeColors.colorBackgroundSecondaryLight};
  }
`;
const MaskedTextInputField = styled(MaskedInput)`
  padding: 12px;
  border: 1px solid
    ${({ hasError }) =>
      hasError
        ? themeColors.colorErrorMessage
        : themeColors.colorBorderPrimary};
  border-style: solid;
  border-radius: ${variables.borderRadius.borderRadius};
  color: ${themeColors.colorTextSecondary};
  font-weight: 400;
  font-size: ${({ fontSize }) =>
    fontSize ?? variables.typography.p.small.fontSize};
  text-transform: ${({ hasSuggestion }) =>
    hasSuggestion ? 'capitalize' : 'none'};
  outline: none;
  pointer-events: ${({ turnOff }) => (turnOff ? 'none' : 'initial')};

  &:focus {
    border-color: ${themeColors.colorSvgSemidark};
  }

  &:disabled {
    background-color: ${themeColors.colorBackgroundSecondaryLight};
  }
`;
const Label = styled(ParagraphNormal)`
  line-height: 2.1;
  font-weight: 600;
`;
export const ErrorMessage = styled(ParagraphNormal)`
  font-size: 0.8rem;
  margin-top: 2px;
  color: ${themeColors.colorErrorMessage};
`;

const Suggestion = styled.span`
  position: absolute;
  color: ${themeColors.colorTextLight};
  font-weight: 400;
  font-size: ${({ fontSize }) =>
    fontSize ?? variables.typography.p.small.fontSize};
  text-transform: capitalize;
  padding: 12px;
  border: 1px solid transparent;
  top: ${({ hasLabel }) => (hasLabel ? '29px' : 'initial')};
`;

const Suffix = styled.span`
  position: absolute;
  font-weight: 400;
  font-size: ${({ fontSize }) =>
    fontSize ?? variables.typography.p.small.fontSize};
  text-transform: capitalize;
  padding: 12px 0;
  border: 1px solid transparent;
  left: 65%;
`;

const Prefix = styled(Suffix)`
  left: 15px;
  top: 29px;
`;

const InputText = forwardRef(
  (
    {
      label,
      maxLength,
      width,
      type,
      error,
      suggestion,
      isMasked,
      mask,
      prefix,
      suffix,
      autocomplete,
      autoFocus,
      onInput,
      ...props
    },
    ref,
  ) => {
    const Input = isMasked ? MaskedTextInputField : TextInputField;
    return (
      <InputContainer width={width}>
        {!!label && <Label>{label}</Label>}
        {!!prefix && <Prefix fontSize={props.fontSize}>{prefix}</Prefix>}
        <Input
          type={type}
          {...props}
          ref={ref}
          hasError={!!error}
          hasSuggestion={!!suggestion}
          hasPrefix={!!prefix}
          mask={mask}
          maxLength={maxLength}
          autoComplete={autocomplete}
          autoFocus={autoFocus}
          onInput={onInput}
        />
        {!!suffix && <Suffix fontSize={props.fontSize}>{suffix}</Suffix>}
        <Suggestion hasLabel={!!label} fontSize={props.fontSize}>
          {suggestion}
        </Suggestion>
        {!!error && <ErrorMessage>{error.message}</ErrorMessage>}
      </InputContainer>
    );
  },
);

InputText.propTypes = {
  isMasked: PropTypes.bool,
  mask: PropTypes.string,
  suggestion: PropTypes.string,
  label: PropTypes.string,
  width: PropTypes.string,
  type: PropTypes.oneOf(['text', 'password', 'email', 'number', 'tel']),
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  autocomplete: PropTypes.string,
  turnOff: PropTypes.bool,
  textAlign: PropTypes.string,
  fontSize: PropTypes.string,
  maxLength: PropTypes.string,
  autoFocus: PropTypes.bool,
  onInput: PropTypes.func,
};

InputText.defaultProps = {
  type: 'text',
  isMasked: false,
  turnOff: false,
};

InputText.displayName = 'InputText';

export default InputText;
